import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Apple, Beef, Wheat, Cherry, Droplet, Pill } from 'lucide-react';

const CATEGORIES = [
  { id: 'Vegetable', label: 'Vegetables', icon: Apple, color: '#65A30D' },
  { id: 'Protein', label: 'Proteins', icon: Beef, color: '#FF6FA8' },
  { id: 'Carbohydrate', label: 'Carbs', icon: Wheat, color: '#FFB800' },
  { id: 'Fruit', label: 'Fruits', icon: Cherry, color: '#FF8A4C' },
  { id: 'Fat', label: 'Fats', icon: Droplet, color: '#7DD3FC' },
  { id: 'Supplement', label: 'Supplements', icon: Pill, color: '#00C8FF' },
];

const TAG_OPTIONS = ['High Protein', 'Fat Loss', 'Recovery', 'Longevity', 'YourReGen Approved'];

export default function AdminFoods() {
  const [foods, setFoods] = useState([]);
  const [tab, setTab] = useState('Vegetable');

  const load = async () => { const r = await api.get('/foods'); setFoods(r.data); };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="admin-foods">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-mini text-[#FFB800]">Flexible Nutrition</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl mt-1">Food Library</h1>
          <p className="text-xs text-muted-foreground mt-1">Build the YourReGen approved food universe.</p>
        </div>
        <FoodForm onSaved={load} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-regen-card border border-[var(--c-hair)] w-full overflow-x-auto justify-start flex-nowrap no-scrollbar">
          {CATEGORIES.map(c => (
            <TabsTrigger key={c.id} value={c.id} data-testid={`food-tab-${c.id}`} className="flex-shrink-0 gap-1.5">
              <c.icon className="w-3.5 h-3.5" style={{ color: c.color }} /> {c.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {CATEGORIES.map(c => (
          <TabsContent key={c.id} value={c.id} className="mt-4">
            <FoodList foods={foods.filter(f => f.category === c.id)} category={c} reload={load} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function FoodList({ foods, category, reload }) {
  const del = async (id) => { if (!confirm('Delete?')) return; await api.delete(`/foods/${id}`); reload(); toast.success('Deleted'); };
  const toggleEnabled = async (food) => {
    await api.put(`/foods/${food.id}`, { ...food, enabled: !food.enabled });
    reload();
  };
  if (foods.length === 0) return <div className="regen-card-elevated p-10 text-center text-muted-foreground text-sm">No {category.label.toLowerCase()} yet. Add the first one.</div>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {foods.map(f => (
        <div key={f.id} className="regen-card-elevated overflow-hidden group" data-testid={`food-${f.id}`}>
          {f.photo_url ? <img src={f.photo_url} alt={f.name} className="w-full h-32 object-cover bg-[var(--c-film)]" /> : <div className="w-full h-32 bg-[var(--c-film)] flex items-center justify-center"><category.icon className="w-8 h-8" style={{ color: category.color }} /></div>}
          <div className="p-3.5">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display font-bold truncate">{f.name}</div>
                <div className="text-xs text-muted-foreground font-mono mt-0.5">{f.serving_size || '—'}</div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {f.regen_score != null && (
                  <span data-testid={`food-score-${f.id}`} className="font-mono text-xs font-bold px-1.5 py-0.5 rounded-md"
                    style={{ color: f.regen_score >= 80 ? '#65A30D' : f.regen_score >= 55 ? '#FFB800' : '#FF5C5C', background: f.regen_score >= 80 ? '#65A30D15' : f.regen_score >= 55 ? '#FFB80015' : '#FF5C5C15' }}
                    title="YourReGen Food Score">{f.regen_score}</span>
                )}
                <Switch checked={f.enabled} onCheckedChange={() => toggleEnabled(f)} data-testid={`food-toggle-${f.id}`} />
              </div>
            </div>
            {f.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {f.tags.map(t => <span key={t} className="pill bg-[var(--c-film)] text-[10px]">{t}</span>)}
              </div>
            )}
            {f.weekly_limit && <div className="text-[11px] text-[#FFB800] mt-2">Limit: {f.weekly_limit}/week</div>}
            <div className="flex gap-1 mt-3">
              <FoodForm food={f} onSaved={reload} />
              <Button size="sm" variant="outline" onClick={() => del(f.id)} className="border-red-500/30 text-red-400 h-8 px-2"><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function FoodForm({ food, onSaved }) {
  const [open, setOpen] = useState(false);
  const empty = { name: '', category: 'Vegetable', photo_url: '', serving_size: '', serving_grams: 100, calories_per_100g: 0, protein_per_100g: 0, carbs_per_100g: 0, fat_per_100g: 0, fiber_per_100g: 0, tags: [], weekly_limit: '', ranking: 100, regen_score: '', enabled: true };
  const [f, setF] = useState(empty);

  const start = () => {
    if (food) setF({ ...empty, ...food, weekly_limit: food.weekly_limit ?? '', regen_score: food.regen_score ?? '' }); else setF(empty);
    setOpen(true);
  };

  const save = async () => {
    if (!f.name) return toast.error('Name required');
    const payload = {
      ...f,
      weekly_limit: f.weekly_limit === '' ? null : parseInt(f.weekly_limit),
      ranking: parseInt(f.ranking) || 100,
      regen_score: f.regen_score === '' || f.regen_score == null ? null : Math.max(1, Math.min(100, parseInt(f.regen_score))),
      serving_grams: parseFloat(f.serving_grams) || 100,
      calories_per_100g: parseFloat(f.calories_per_100g) || 0,
      protein_per_100g: parseFloat(f.protein_per_100g) || 0,
      carbs_per_100g: parseFloat(f.carbs_per_100g) || 0,
      fat_per_100g: parseFloat(f.fat_per_100g) || 0,
      fiber_per_100g: parseFloat(f.fiber_per_100g) || 0,
    };
    try {
      if (food) await api.put(`/foods/${food.id}`, payload); else await api.post('/foods', payload);
      toast.success('Saved'); setOpen(false); onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleTag = (t) => setF({ ...f, tags: f.tags.includes(t) ? f.tags.filter(x => x !== t) : [...f.tags, t] });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size={food ? 'sm' : 'default'} variant={food ? 'outline' : 'default'}
          onClick={start}
          data-testid={food ? `edit-food-${food.id}` : 'add-food-btn'}
          className={food ? 'border-[var(--c-hair)] h-8 px-2 flex-1' : 'bg-[#FFB800] text-[#081726] font-semibold'}>
          {food ? <Edit2 className="w-3 h-3" /> : <><Plus className="w-4 h-4 mr-2" /> Add Food</>}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{food ? 'Edit' : 'New'} Food</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={f.name} onChange={e => setF({...f, name: e.target.value})} data-testid="food-name" className="bg-transparent border-[var(--c-hair)]" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={f.category} onValueChange={v => setF({...f, category: v})}>
                <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="food-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Serving Size</Label><Input value={f.serving_size} onChange={e => setF({...f, serving_size: e.target.value})} placeholder="150 g" className="bg-transparent border-[var(--c-hair)]" /></div>
          </div>
          <div><Label>Photo URL (optional — leave blank for placeholder)</Label><Input value={f.photo_url} onChange={e => setF({...f, photo_url: e.target.value})} className="bg-transparent border-[var(--c-hair)]" /></div>
          {f.photo_url && <img src={f.photo_url} className="w-full max-h-32 object-cover rounded-lg" alt="" />}

          <div className="regen-card p-3">
            <div className="label-mini mb-2">Macros (per 100 g)</div>
            <div className="grid grid-cols-5 gap-2">
              <div><Label className="text-[10px]">Cal</Label><Input type="number" step="0.1" value={f.calories_per_100g} onChange={e => setF({...f, calories_per_100g: e.target.value})} data-testid="food-cal" className="bg-transparent border-[var(--c-hair)] font-mono text-xs h-9" /></div>
              <div><Label className="text-[10px]">Prot</Label><Input type="number" step="0.1" value={f.protein_per_100g} onChange={e => setF({...f, protein_per_100g: e.target.value})} data-testid="food-protein" className="bg-transparent border-[var(--c-hair)] font-mono text-xs h-9" /></div>
              <div><Label className="text-[10px]">Carbs</Label><Input type="number" step="0.1" value={f.carbs_per_100g} onChange={e => setF({...f, carbs_per_100g: e.target.value})} data-testid="food-carbs" className="bg-transparent border-[var(--c-hair)] font-mono text-xs h-9" /></div>
              <div><Label className="text-[10px]">Fat</Label><Input type="number" step="0.1" value={f.fat_per_100g} onChange={e => setF({...f, fat_per_100g: e.target.value})} data-testid="food-fat" className="bg-transparent border-[var(--c-hair)] font-mono text-xs h-9" /></div>
              <div><Label className="text-[10px]">Fiber</Label><Input type="number" step="0.1" value={f.fiber_per_100g} onChange={e => setF({...f, fiber_per_100g: e.target.value})} data-testid="food-fiber" className="bg-transparent border-[var(--c-hair)] font-mono text-xs h-9" /></div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Weekly Limit (optional)</Label><Input type="number" value={f.weekly_limit} onChange={e => setF({...f, weekly_limit: e.target.value})} placeholder="∞" className="bg-transparent border-[var(--c-hair)] font-mono" /></div>
            <div><Label>Ranking (1-100)</Label><Input type="number" value={f.ranking} onChange={e => setF({...f, ranking: e.target.value})} className="bg-transparent border-[var(--c-hair)] font-mono" /></div>
          </div>
          <div>
            <Label>YourReGen Food Score (1-100)</Label>
            <Input type="number" min="1" max="100" value={f.regen_score} onChange={e => setF({...f, regen_score: e.target.value})} placeholder="Auto" data-testid="food-regen-score" className="bg-transparent border-[var(--c-hair)] font-mono" />
            <p className="text-[10px] text-muted-foreground mt-1">Leave blank to auto-calculate. Higher = more nutrient-dense (Broccoli 100, White Bread 40, Soft Drink 5).</p>
          </div>
          <div>
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {TAG_OPTIONS.map(t => (
                <button key={t} onClick={() => toggleTag(t)} data-testid={`tag-${t}`}
                  className={`pill border ${f.tags.includes(t) ? 'bg-[var(--c-green-soft)] border-[var(--c-green)] text-[var(--c-green-ink)]' : 'border-[var(--c-hair)] text-muted-foreground'}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--c-film)]">
            <div>
              <div className="text-sm font-medium">Enabled</div>
              <div className="text-xs text-muted-foreground">Available for clients to choose</div>
            </div>
            <Switch checked={f.enabled} onCheckedChange={v => setF({...f, enabled: v})} />
          </div>
          <Button onClick={save} data-testid="food-save" className="w-full h-11 bg-[#FFB800] text-[#081726] font-semibold">Save Food</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
